// DC-DC Converter Sizing Tool: topology/thermal/redundancy presets, load-row enums,
// field metadata + client-side per-field validation (scalar fields only, for instant
// inline feedback -- mirrors fuseRatingTools.js). Load-row validation is
// server-authoritative and non-blocking (mirrors canBusLoadTools.js): an invalid row
// is flagged by the backend response and excluded from the sizing, and the backend
// also returns an explicit "spec is undersized" warning for it.
// The actual sizing math (margin waterfall, feasibility checks, sensitivity) lives in
// the main backend -- see src/lib/toolsApi.js (dcdcSizingToolApi) and
// backend/tools/dcdc_sizing_tool.py. Mirrors backend/core/lookup/dcdc_sizing_specs.py.

let _rowSeq = 0;
export function nextRowId() {
  _rowSeq += 1;
  return `row-${Date.now()}-${_rowSeq}`;
}

export const TOPOLOGY_PRESETS = [
  {
    value: 'hv_to_lv',
    label: 'HV → LV (400V Traction Battery → 12V Bus)',
    description: '400V-class traction battery supplying the 12V vehicle bus.',
    source: { nominalVoltageV: 400, minVoltageV: 250, maxVoltageV: 450, transientDropoutFloorV: 220, transientDropoutDurationMs: 10, transientOvervoltageV: 470, openCircuitVoltageV: 350, internalResistanceOhm: 0.1, sagCurrentA: 300 },
    output: { nominalVoltageV: 12, regulationTolerancePercent: 5, minVoltageBeforeLoadShedV: 9, rippleRequirementMv: 200, batteryChargeCurrentA: 10 },
    candidate: { inputMinV: 200, inputMaxV: 470, inputAbsMaxV: 500 },
    thermalPreset: 'liquid_coolant',
  },
  {
    value: 'hv800_to_lv',
    label: 'HV → LV (800V Traction Battery → 12V Bus)',
    description: '800V-class traction battery supplying the 12V vehicle bus.',
    source: { nominalVoltageV: 800, minVoltageV: 550, maxVoltageV: 850, transientDropoutFloorV: 500, transientDropoutDurationMs: 10, transientOvervoltageV: 900, openCircuitVoltageV: 700, internalResistanceOhm: 0.2, sagCurrentA: 300 },
    output: { nominalVoltageV: 12, regulationTolerancePercent: 5, minVoltageBeforeLoadShedV: 9, rippleRequirementMv: 200, batteryChargeCurrentA: 10 },
    candidate: { inputMinV: 450, inputMaxV: 900, inputAbsMaxV: 950 },
    thermalPreset: 'liquid_coolant',
  },
  {
    value: 'lv_to_lv',
    label: 'LV → LV (48V Mild-Hybrid Bus → 12V Bus)',
    description: '48V mild-hybrid bus supplying the 12V vehicle bus.',
    source: { nominalVoltageV: 48, minVoltageV: 36, maxVoltageV: 58, transientDropoutFloorV: 24, transientDropoutDurationMs: 20, transientOvervoltageV: 60, openCircuitVoltageV: 44, internalResistanceOhm: 0.02, sagCurrentA: 200 },
    output: { nominalVoltageV: 12, regulationTolerancePercent: 5, minVoltageBeforeLoadShedV: 9, rippleRequirementMv: 150, batteryChargeCurrentA: 10 },
    candidate: { inputMinV: 24, inputMaxV: 60, inputAbsMaxV: 70 },
    thermalPreset: 'underhood_moderate',
  },
  {
    value: 'lv_boost_12_to_48',
    label: 'LV → LV Boost (12V Bus → 48V Bus, reverse direction)',
    description: '12V bus back-feeding the 48V bus — the reverse direction of a bidirectional 48V/12V unit, sized as its own case.',
    source: { nominalVoltageV: 12, minVoltageV: 9, maxVoltageV: 16, transientDropoutFloorV: 6, transientDropoutDurationMs: 20, transientOvervoltageV: 24, openCircuitVoltageV: 12.2, internalResistanceOhm: 0.01, sagCurrentA: 100 },
    output: { nominalVoltageV: 48, regulationTolerancePercent: 5, minVoltageBeforeLoadShedV: 36, rippleRequirementMv: 500, batteryChargeCurrentA: 5 },
    candidate: { inputMinV: 6, inputMaxV: 18, inputAbsMaxV: 40 },
    thermalPreset: 'underhood_moderate',
  },
];
export const TOPOLOGY_BY_VALUE = Object.fromEntries(TOPOLOGY_PRESETS.map((t) => [t.value, t]));

export const THERMAL_PRESETS = [
  { value: 'liquid_coolant', label: 'Liquid-Cooled (coolant inlet temp)', t1C: 65, t2C: 85, t2PercentRating: 50 },
  { value: 'cabin', label: 'Cabin / Interior, air-cooled (mild)', t1C: 25, t2C: 70, t2PercentRating: 85 },
  { value: 'underhood_moderate', label: 'Underhood, air-cooled (moderate)', t1C: 25, t2C: 85, t2PercentRating: 70 },
  { value: 'underhood_severe', label: 'Underhood, air-cooled (severe, near-engine)', t1C: 25, t2C: 105, t2PercentRating: 50 },
  { value: 'custom', label: 'Custom Curve…', t1C: 25, t2C: 85, t2PercentRating: 70 },
];
export const THERMAL_PRESET_BY_VALUE = Object.fromEntries(THERMAL_PRESETS.map((t) => [t.value, t]));

export const REDUNDANCY_OPTIONS = [
  { value: 'none', label: 'None (Single Converter)' },
  { value: 'n_plus_1_full', label: 'N+1 (2 Units, Each Full Rating)' },
  { value: 'load_share_2up', label: 'Load-Share (2 Units, Each Half Rating)' },
];

export const CRITICALITY_OPTIONS = [
  { value: 'safety_critical', label: 'Safety-Critical' },
  { value: 'comfort', label: 'Comfort' },
  { value: 'optional', label: 'Optional' },
];

export const DUTY_PATTERNS = [
  { value: 'continuous', label: 'Continuous' },
  { value: 'intermittent', label: 'Intermittent' },
  { value: 'cyclic', label: 'Cyclic (Periodic)' },
];

export const EFFICIENCY_CURVE_LOAD_PERCENTS = [20, 50, 75, 100];
export const DEFAULT_EFFICIENCY_CURVE = [
  { loadPercent: 20, efficiencyPercent: 85 },
  { loadPercent: 50, efficiencyPercent: 90 },
  { loadPercent: 75, efficiencyPercent: 92 },
  { loadPercent: 100, efficiencyPercent: 90 },
];

export function makeLoadRow(overrides = {}) {
  return {
    id: nextRowId(),
    name: '',
    inputMode: 'power',
    continuousPowerW: 0,
    continuousCurrentA: 0,
    peakInputMode: 'power',
    peakPowerW: 0,
    peakCurrentA: 0,
    peakDurationMs: 0,
    dutyCyclePercent: 100,
    dutyPattern: 'continuous',
    criticality: 'optional',
    coincidentWithPeak: false,
    ...overrides,
  };
}

export const DEFAULT_LOAD_ROWS = [
  makeLoadRow({ name: 'ECU / Body Controller', inputMode: 'current', continuousCurrentA: 3, peakInputMode: 'current', peakCurrentA: 6, peakDurationMs: 500, dutyCyclePercent: 100, criticality: 'safety_critical', coincidentWithPeak: true }),
  makeLoadRow({ name: 'Electric Power Steering', inputMode: 'power', continuousPowerW: 150, peakInputMode: 'power', peakPowerW: 800, peakDurationMs: 2000, dutyCyclePercent: 30, dutyPattern: 'intermittent', criticality: 'safety_critical', coincidentWithPeak: true }),
  makeLoadRow({ name: 'Infotainment / HVAC Blower', inputMode: 'power', continuousPowerW: 200, peakInputMode: 'power', peakPowerW: 300, peakDurationMs: 1000, dutyCyclePercent: 80, criticality: 'comfort', coincidentWithPeak: false }),
];

const _defaultTopology = TOPOLOGY_BY_VALUE.hv_to_lv;
const _defaultThermal = THERMAL_PRESET_BY_VALUE[_defaultTopology.thermalPreset];

// Scalar fields a topology preset re-seeds when it is selected (field key -> value).
export function topologySeedValues(preset) {
  return {
    sourceNominalVoltageV: preset.source.nominalVoltageV,
    sourceMinVoltageV: preset.source.minVoltageV,
    sourceMaxVoltageV: preset.source.maxVoltageV,
    transientDropoutFloorV: preset.source.transientDropoutFloorV,
    transientDropoutDurationMs: preset.source.transientDropoutDurationMs,
    transientOvervoltageV: preset.source.transientOvervoltageV,
    openCircuitVoltageV: preset.source.openCircuitVoltageV,
    internalResistanceOhm: preset.source.internalResistanceOhm,
    sagCurrentA: preset.source.sagCurrentA,
    outputNominalVoltageV: preset.output.nominalVoltageV,
    outputRegulationTolerancePercent: preset.output.regulationTolerancePercent,
    outputMinVoltageBeforeLoadShedV: preset.output.minVoltageBeforeLoadShedV,
    rippleRequirementMv: preset.output.rippleRequirementMv,
    batteryChargeCurrentA: preset.output.batteryChargeCurrentA,
    candidateInputMinV: preset.candidate.inputMinV,
    candidateInputMaxV: preset.candidate.inputMaxV,
    candidateInputAbsMaxV: preset.candidate.inputAbsMaxV,
  };
}

export const DEFAULT_INPUTS = {
  topology: 'hv_to_lv',
  ...topologySeedValues(_defaultTopology),
  useSagModel: false,
  lvBatteryPeakSharePercent: 0,
  safetyMarginPercent: 25,
  growthMarginPercent: 15,
  sensitivityPerturbPercent: 10,
  thermalPreset: _defaultTopology.thermalPreset,
  thermalT1C: _defaultThermal.t1C,
  thermalT2C: _defaultThermal.t2C,
  thermalT2PercentRating: _defaultThermal.t2PercentRating,
  maxAmbientC: _defaultThermal.t2C,
  thermalTimeConstantS: 20,
  deratePeakTrack: false,
  applyDutyCycleCredit: true,
  redundancyMode: 'none',
  currentShareImbalancePercent: 10,
  efficiencyPenaltyAtMinVoltagePts: 1.5,
  assumedHoldupCapacitanceUf: 0,
};

// min/max mirror NUMERIC_FIELD_SPECS in backend/core/lookup/dcdc_sizing_specs.py.
export const NUMERIC_FIELD_SPECS = [
  { key: 'sourceNominalVoltageV', label: 'Source Nominal Voltage', unit: 'V', min: 1, max: 1500, strictlyPositive: true },
  { key: 'sourceMinVoltageV', label: 'Source Min Operating Voltage', unit: 'V', min: 1, max: 1500, strictlyPositive: true },
  { key: 'sourceMaxVoltageV', label: 'Source Max Operating Voltage', unit: 'V', min: 1, max: 1500, strictlyPositive: true },
  { key: 'openCircuitVoltageV', label: 'Open-Circuit Voltage (worst-case SOC)', unit: 'V', min: 1, max: 1500, strictlyPositive: true },
  { key: 'internalResistanceOhm', label: 'Internal Resistance', unit: 'Ω', min: 0, max: 10, strictlyPositive: false },
  { key: 'sagCurrentA', label: 'Other Loads’ Current on Source', unit: 'A', min: 0, max: 5000, strictlyPositive: false },
  { key: 'transientDropoutFloorV', label: 'Transient Dropout Floor Voltage', unit: 'V', min: 0, max: 1500, strictlyPositive: false },
  { key: 'transientDropoutDurationMs', label: 'Transient Dropout Duration', unit: 'ms', min: 0, max: 5000, strictlyPositive: false },
  { key: 'transientOvervoltageV', label: 'Transient Overvoltage Peak', unit: 'V', min: 0, max: 1500, strictlyPositive: false },
  { key: 'outputNominalVoltageV', label: 'Output Nominal Voltage', unit: 'V', min: 1, max: 1000, strictlyPositive: true },
  { key: 'outputRegulationTolerancePercent', label: 'Output Regulation Tolerance', unit: '%', min: 0, max: 50, strictlyPositive: false },
  { key: 'outputMinVoltageBeforeLoadShedV', label: 'Min Output Voltage Before Load-Shed', unit: 'V', min: 0, max: 1000, strictlyPositive: false },
  { key: 'rippleRequirementMv', label: 'Ripple/Noise Requirement', unit: 'mV', min: 0, max: 5000, strictlyPositive: false },
  { key: 'batteryChargeCurrentA', label: 'LV Battery Charging Current', unit: 'A', min: 0, max: 1000, strictlyPositive: false },
  { key: 'lvBatteryPeakSharePercent', label: 'Peak Excess Supplied by LV Battery', unit: '%', min: 0, max: 100, strictlyPositive: false },
  { key: 'safetyMarginPercent', label: 'Safety / Design Margin', unit: '%', min: 0, max: 200, strictlyPositive: false },
  { key: 'growthMarginPercent', label: 'Growth / Future-Proofing Margin', unit: '%', min: 0, max: 200, strictlyPositive: false },
  { key: 'sensitivityPerturbPercent', label: 'Sensitivity Perturbation', unit: '%', min: 1, max: 50, strictlyPositive: true },
  { key: 'thermalT1C', label: 'Thermal Reference Temp (T1)', unit: '°C', min: -40, max: 150, strictlyPositive: false },
  { key: 'thermalT2C', label: 'Thermal Elevated Temp (T2)', unit: '°C', min: -40, max: 200, strictlyPositive: false },
  { key: 'thermalT2PercentRating', label: 'Rating Retained at T2', unit: '%', min: 1, max: 100, strictlyPositive: true },
  { key: 'maxAmbientC', label: 'Max Operating Ambient / Coolant Temp', unit: '°C', min: -40, max: 200, strictlyPositive: false },
  { key: 'thermalTimeConstantS', label: 'Converter Thermal Time Constant', unit: 's', min: 0.1, max: 3600, strictlyPositive: true },
  { key: 'currentShareImbalancePercent', label: 'Load-Share Current Imbalance', unit: '%', min: 0, max: 50, strictlyPositive: false },
  { key: 'efficiencyPenaltyAtMinVoltagePts', label: 'Efficiency Drop at Min Input Voltage', unit: 'pts', min: 0, max: 20, strictlyPositive: false },
  { key: 'candidateInputMinV', label: 'Candidate Converter Min Input Voltage (UVLO)', unit: 'V', min: 0, max: 1500, strictlyPositive: false },
  { key: 'candidateInputMaxV', label: 'Candidate Converter Max Operating Input Voltage', unit: 'V', min: 0, max: 1500, strictlyPositive: true },
  { key: 'candidateInputAbsMaxV', label: 'Candidate Converter Absolute-Max Input Voltage', unit: 'V', min: 0, max: 1500, strictlyPositive: true },
  { key: 'assumedHoldupCapacitanceUf', label: 'Assumed Input Hold-Up Capacitance', unit: 'µF', min: 0, max: 10000000, strictlyPositive: false },
];

export function validateField(spec, rawValue) {
  if (rawValue === '' || rawValue === null || rawValue === undefined) {
    return 'Required.';
  }
  const value = Number(rawValue);
  if (Number.isNaN(value)) {
    return 'Must be a number.';
  }
  if (spec.strictlyPositive && value <= 0) {
    return 'Must be greater than 0.';
  }
  if (spec.min !== null && value < spec.min) {
    return `Must be ≥ ${spec.min}.`;
  }
  if (spec.max !== null && value > spec.max) {
    return `Must be ≤ ${spec.max}.`;
  }
  return null;
}
